import PropTypes from "prop-types";
import { ChoosePlanModalContainer, PillToggle } from "./ChoosePlanModal.styles";
import { useEffect, useMemo, useState } from "react";
import { usePlans } from "features/Plans/hook/usePlans";
import { Loader } from "components/Loader/index";
import { contains, isLoggedIn, getPlanShortName } from "utils";
import { useDispatch, useSelector } from "react-redux";
import { setTheme } from "Layout/slice";
import { useSearchParams } from "react-router-dom";
import {
  BILLING_INTERVAL,
  STRIPE_CANCEL_URL,
  STRIPE_SUCCESS_URL,
  THEME_TYPES,
} from "utils/constants";
import { ModernPlanCard } from "features/Plans/components/ModernPlanCard/index";
import {
  subscribeToPremiumPlan,
  unsubscribePlan,
  continuePremiumPlan,
} from "features/Plans/service";
import { openNewTab, parseErrorMessage } from "utils/index";
import { toast as sonnerToast } from "sonner";
import { trackEvent } from "utils/tracking";
import { userDetailSelect } from "Layout/slice/selector";
import { Calendar } from "lucide-react";
import { unixToDateTime, FULL_MONTH_YEAR_FORMAT } from "utils/date";
import { ReactivateModal } from "./ReactivateModal";

export const ChoosePlanModal = ({ onHide, show = true }) => {
  const dispatch = useDispatch();
  const userDetails = useSelector(userDetailSelect);

  const currentPlan = userDetails?.surmount_plan;
  const currentPlanName = currentPlan?.name;
  const subscriptionEnd = userDetails?.stripe_subscription?.current_period_end;
  const renewalDate =
    subscriptionEnd && unixToDateTime(subscriptionEnd, FULL_MONTH_YEAR_FORMAT);

  const upcomingPlan = userDetails?.upcoming_plan;
  const cancelAtPeriodEnd =
    userDetails?.stripe_subscription?.cancel_at_period_end;
  const freeDowngradeScheduled = !!cancelAtPeriodEnd && !upcomingPlan;

  const [searchParams] = useSearchParams();
  const planParam = searchParams.get("plan");

  const { isLoading, plans } = usePlans();
  const [duration, setDuration] = useState(BILLING_INTERVAL.YEARLY);
  const isAnnual = duration === BILLING_INTERVAL.YEARLY;
  const [planCheckoutId, setPlanCheckoutId] = useState("");
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const [isReactivating, setIsReactivating] = useState(false);

  const upcomingPlanDetails = useMemo(() => {
    if (!upcomingPlan?.plan) return null;
    return Object.values(plans).find((p) => p.id === upcomingPlan.plan) || null;
  }, [upcomingPlan, plans]);

  const upcomingPlanLabel = useMemo(() => {
    if (!upcomingPlanDetails) return null;
    const short = getPlanShortName(upcomingPlanDetails.name);
    if (!upcomingPlanDetails.interval || short === "Free")
      return `${short} plan`;
    const interval =
      upcomingPlanDetails.interval === BILLING_INTERVAL.YEARLY
        ? "yearly"
        : "monthly";
    return `${short} ${interval} plan`;
  }, [upcomingPlanDetails]);

  useEffect(() => {
    if (!isLoggedIn()) {
      dispatch(setTheme(THEME_TYPES.LIGHT));
    }
    if (planParam !== null) {
      setDuration(
        contains(planParam, BILLING_INTERVAL.YEARLY)
          ? BILLING_INTERVAL.YEARLY
          : BILLING_INTERVAL.MONTHLY
      );
      handlePlanSubscription(planParam);
    }
  }, []);

  const handlePlanSubscription = async (planId, { successMessage } = {}) => {
    const plan = Object.values(plans).find((p) => p.id === planId);
    const interval = plan?.interval ?? duration;
    try {
      trackEvent({
        name: "choose_plan",
        label: "Plans",
        plan_id: planId,
        interval,
        price: plan?.price,
      });
      setPlanCheckoutId(planId);
      const params = {
        plan: planId,
        interval,
        success_url: STRIPE_SUCCESS_URL,
        cancel_url: STRIPE_CANCEL_URL,
      };
      const response = await subscribeToPremiumPlan(params);
      if (response.url) {
        trackEvent({
          name: "start_subscription",
          label: "Plans",
          plan_id: planId,
          interval,
          price: plan?.price,
        });
        openNewTab(response.url, "_self");
      } else {
        trackEvent({
          name: "order_complete",
          label: "Plans",
          plan_id: planId,
          interval,
          price: plan?.price,
        });
        sonnerToast.success(successMessage || response.message);
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }
    } catch (error) {
      const errorMessage = parseErrorMessage(error);
      trackEvent({
        name: "payment_failed",
        label: "Plans",
        plan_id: planId,
        error: errorMessage,
      });
      sonnerToast.error(errorMessage);
    } finally {
      setPlanCheckoutId("");
    }
  };

  const handleDowngradeToFree = async () => {
    try {
      await unsubscribePlan(null);
      sonnerToast.success("Downgrade to Surmount Free scheduled");
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error) {
      sonnerToast.error(parseErrorMessage(error));
    }
  };

  const handleReactivate = async () => {
    setIsReactivating(true);
    try {
      await continuePremiumPlan();
      sonnerToast.success("Subscription reactivated");
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error) {
      sonnerToast.error(parseErrorMessage(error));
    } finally {
      setIsReactivating(false);
      setShowReactivateModal(false);
    }
  };

  const handleToggleChange = () => {
    setDuration((d) =>
      d === BILLING_INTERVAL.YEARLY
        ? BILLING_INTERVAL.MONTHLY
        : BILLING_INTERVAL.YEARLY
    );
  };

  const { durationPlans, strikePriceMap } = useMemo(() => {
    const allPlans = Object.values(plans);
    const filtered = allPlans.filter(
      (p) => p.interval === duration || p.interval === null
    );
    const priceMap = allPlans
      .filter((p) => p.interval === BILLING_INTERVAL.YEARLY)
      .reduce((acc, p) => {
        const monthly = allPlans.find(
          (m) =>
            m.id ===
            p.id.replace(
              `_${BILLING_INTERVAL.YEARLY}`,
              `_${BILLING_INTERVAL.MONTHLY}`
            )
        );
        if (monthly) acc[p.id] = monthly.price * 12;
        return acc;
      }, {});
    return { durationPlans: filtered, strikePriceMap: priceMap };
  }, [plans, duration]);

  const downgradePlanLabel = freeDowngradeScheduled
    ? "Free plan"
    : upcomingPlanLabel || "new plan";

  const scheduledNotice = (upcomingPlan || freeDowngradeScheduled) &&
    renewalDate && (
      <div className="renewal-notice scheduled">
        <Calendar size={14} className="renewal-icon" />
        <span>
          Your downgrade to the {downgradePlanLabel} is scheduled for{" "}
          {renewalDate}.
        </span>
        <button
          className="reactivate-btn"
          onClick={() => setShowReactivateModal(true)}
          disabled={isReactivating}
        >
          {isReactivating && (
            <Loader className="inline-loader" variant="light" />
          )}
          Reactivate current {getPlanShortName(currentPlanName)} plan
        </button>
      </div>
    );

  const autoRenewNotice = !upcomingPlan &&
    !freeDowngradeScheduled &&
    renewalDate &&
    currentPlanName && (
      <div className="renewal-notice">
        <Calendar size={14} className="renewal-icon" />
        <span>
          Your {currentPlanName} plan auto-renews on {renewalDate}.
        </span>
      </div>
    );

  return (
    <ChoosePlanModalContainer onHide={onHide} show={show}>
      <div className="heading">
        <h2>Plans that grow with you</h2>
        {scheduledNotice || autoRenewNotice}
      </div>

      {isLoading ? (
        <Loader />
      ) : (
        <div className="plan-container">
          <div className="plan-toggle">
            <PillToggle>
              <button
                className={`toggle-pill ${!isAnnual ? "active" : ""}`}
                onClick={() => isAnnual && handleToggleChange()}
              >
                Monthly
              </button>
              <button
                className={`toggle-pill ${isAnnual ? "active" : ""}`}
                onClick={() => !isAnnual && handleToggleChange()}
              >
                Yearly <span className="save-text">· Save 2 months</span>
              </button>
            </PillToggle>
          </div>

          <div className="plan-wrapper">
            {durationPlans.map((plan, inx) => (
              <ModernPlanCard
                plan={plan}
                key={plan.id}
                index={inx}
                duration={duration}
                planCheckoutId={planCheckoutId}
                handlePlanSubscription={handlePlanSubscription}
                strikePrice={strikePriceMap[plan.id] ?? null}
                upcomingPlanId={upcomingPlan?.plan ?? null}
                periodEnd={renewalDate}
                currentPlanName={currentPlanName}
                onDowngradeToFree={handleDowngradeToFree}
                freeDowngradeScheduled={freeDowngradeScheduled}
              />
            ))}
          </div>

          <p className="us-residents-disclaimer">
            Prices and plans are subject to change at Surmount&apos;s
            discretion.
          </p>
        </div>
      )}

      <ReactivateModal
        show={showReactivateModal}
        onHide={() => setShowReactivateModal(false)}
        onConfirm={handleReactivate}
        currentPlan={currentPlan}
        renewalDate={renewalDate}
      />
    </ChoosePlanModalContainer>
  );
};

ChoosePlanModal.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func,
};

ChoosePlanModal.displayName = "ChoosePlanModal";
